/**
 * Copyright (C) 2021 THL A29 Limited, a Tencent company.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import ParagraphBase from '@/core/ParagraphBase';
import { compileRegExp, htmlBlockReg } from '@/utils/regexp';
import getHookInstance from '@/utils/getHookInstance';
import List from './List';
import CodeBlock from './CodeBlock';
import Table from './Table';
const listHeadRegStr = '(?:[ |\u00a0]{0,3}(?:[*+-]|\\d+[.]|[a-z]\\.|[I一二三四五六七八九十]+\\.)[ \\t\u00a0]+)';

function computeLeadingSpaces(leadingChars) {
  const indentRegex = /^(\t|[ |\u00a0]{1,4})/;
  let leadingCharsTemp = leadingChars;
  let indent = 0;
  while (indentRegex.test(leadingCharsTemp)) {
    leadingCharsTemp = leadingCharsTemp.replace(/^(\t|[ |\u00a0]{1,4})/g, '');
    indent += 1;
  }
  return indent;
}

export default class Blockquote extends ParagraphBase {
  static HOOK_NAME = 'blockquote';

  constructor() {
    super({ needCache: true });
    // TODO: String.prototype.repeat polyfill
  }

  // 去除+转换列表内容前置\t，保障对齐
  flatListIndent(list = []) {
    let replaceText = '';
    list.forEach((str, i) => {
      // 将 /t 转换成 空格
      const $str = str.replace(/^[ \t]*/, (match) => {
        return match
          .split('')
          .map((v) => (v === '\t' ? '    ' : ' '))
          .join('');
      });
      if (i === 0) {
        // 第一项\t内容去除去除
        list[0] = $str.replace(/^[ ]*/, (match) => {
          replaceText = match;
          return '';
        });
      } else {
        // 后续项同步处理
        if (~$str.indexOf(replaceText)) {
          list[i] = $str.substr(replaceText.length);
        } else {
          list[i] = $str.replace(/^[ ]*/, '');
        }
      }
    });
  }

  processList(list = []) {
    console.log('processList:', list, this);
    if (!list.length) return '';
    if (!this.$engine) return '';
    this.flatListIndent(list);
    const { $engine } = this;
    const listHook = getHookInstance($engine, 'paragraph', List);
    const listStr = list.join('\n');
    const $dealSentenceByCache = $engine.$dealSentenceByCache.bind($engine);
    // 处理列表情况
    const $html1 = listHook.makeHtml(listStr, $dealSentenceByCache, false);
    const $html2 = listHook.afterMakeHtml($html1, false);
    console.log('html', [$html1, $html2]);
    return $html2;
  }

  // 处理代码块
  codeBlockAfterMakeHtml(str) {
    const cbHook = getHookInstance(this.$engine, 'paragraph', CodeBlock);
    const $html1 = cbHook.afterMakeHtml(str, false);
    console.log('[bq] codeBlockAfterMakeHtml', [$html1]);
    return $html1;
  }

  // 处理表格
  tableAfterMakeHtml(str) {
    const tableHook = getHookInstance(this.$engine, 'paragraph', Table);
    const $html1 = tableHook.afterMakeHtml(str, false);
    console.log('[bq] tableAfterMakeHtml', [$html1]);
    return $html1;
  }

  handleMatch(str, sentenceMakeFunc) {
    return str.replace(this.RULE.reg, (match, lines, content) => {
      // 处理 match 中的列表开头项
      const $listHeadReg = new RegExp(listHeadRegStr);
      const $r = $listHeadReg.exec(match);
      let start = '';
      if ($r && $r.index === 0) {
        // 开头匹配到了
        // eslint-disable-next-line prefer-destructuring
        start = $r[0];
        // eslint-disable-next-line no-param-reassign
        match = match.substr(start.length);
      }
      const { sign: contentSign, html: parsedHtml } = sentenceMakeFunc(content);
      const sign = this.signWithCache(parsedHtml) || contentSign;
      const lineCount = this.getLineCount(match, lines, true); // 段落所占行数
      const listRegex =
        /^>+(([ \t]*([*+-]|\d+[.]|[a-z]\.|[I一二三四五六七八九十]+\.)[ \t]+)([^\r]+?)($|\n{2,}(?=\S)(?![ \t]*(?:[*+-]|\d+[.]|[a-z]\.|[I一二三四五六七八九十]+\.)[ \t]+)))/;
      let lastIndent = computeLeadingSpaces(lines);
      // 逐行处理
      const contentLines = parsedHtml.split('\n');
      const replaceReg = /^[>\s]+/;
      const countReg = />/g;
      let lastLevel = 1;
      let level = 0;
      let handledHtml = `<blockquote data-sign="${sign}_${lineCount}" data-lines="${lineCount}">`;
      const list = []; // 列表数据存储
      let preHtml = '';
      for (let i = 0; contentLines[i]; i++) {
        if (i !== 0) {
          const leadIndent = computeLeadingSpaces(contentLines[i]);
          if (leadIndent <= lastIndent && listRegex.test(contentLines[i])) {
            // list just not support yet, just show the origin code
            // break;
          }
          lastIndent = leadIndent;
        }
        /* eslint-disable no-loop-func */
        const $line = contentLines[i].replace(replaceReg, (leadSymbol) => {
          const leadSymbols = leadSymbol.match(countReg);
          // 本行引用嵌套层级比上层要多
          if (leadSymbols && leadSymbols.length > lastLevel) {
            level = leadSymbols.length;
          } else {
            // 否则保持当前缩进层级
            level = lastLevel;
          }
          return '';
        });

        // 补充缩进
        if (lastLevel < level) {
          handledHtml += '<blockquote>'.repeat(level - lastLevel);
          lastLevel = level;
        }
        // 列表处理
        if (listRegex.test(contentLines[i])) {
          // 符合列表，去除 > 符号
          list.push(contentLines[i].replace(/^>+/, ''));
          if (i === contentLines.length - 1) {
            preHtml = this.processList([...list]);
            handledHtml += preHtml;
            list.length = 0;
          }
          continue;
        }

        if (list.length > 0) {
          preHtml = this.processList([...list]);
          handledHtml += preHtml;
          list.length = 0;
        } else if (lastLevel === level && i !== 0) {
          // 同层级，且不为首行时补充一个换行
          if (!htmlBlockReg.test(preHtml) && !(i === 1 && /^>\s*$/.test(contentLines[0]))) {
            handledHtml += '<br>';
          }
        }

        // 插入当前行内容
        // 恢复代码块
        preHtml = this.codeBlockAfterMakeHtml($line);
        // 恢复表格
        preHtml = this.tableAfterMakeHtml(preHtml);
        handledHtml += preHtml;
      }
      // 标签闭合
      handledHtml += '</blockquote>'.repeat(lastLevel);
      return start + this.getCacheWithSpace(this.pushCache(handledHtml, sign, lineCount), match);
    });
  }

  makeHtml(str, sentenceMakeFunc) {
    console.log('blockquote makeHtml start', str);
    if (!this.test(str)) {
      return str;
    }
    const $html = this.handleMatch(str, sentenceMakeFunc);
    console.log('blockquote makeHtml end', $html);
    return $html;
  }

  rule() {
    const ret = {
      // 增加列表开头的规则匹配
      begin: `(?:^|\\n|${listHeadRegStr})(\\s*)`,
      content: [
        '(',
        '>(?:.+?\\n(?![*+-]|\\d+[.]|[a-z]\\.))(?:>*.+?\\n(?![*+-]|\\d+[.]|[a-z]\\.))*(?:>*.+?)', // multiline
        '|', // or
        '>(?:.+?)', // single line
        ')',
      ].join(''),
      end: '(?=(\\n)|$)',
    };
    ret.reg = compileRegExp(ret, 'g');
    return ret;
  }
}
